import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Check, Loader2, ArrowRight, UserPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const CONTRACT_OPTIONS = [
  { months: 6, label: '6 mån', multiplier: 1.10, badge: '+10%' },
  { months: 12, label: '12 mån', multiplier: 1.0, badge: 'Standard' },
  { months: 24, label: '24 mån', multiplier: 0.85, badge: '−15%' },
];

const PLAN_PER_EMPLOYEE = { start: 199, pro: 149, business: 99 };

const SalesNewCustomer = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    company_name: '', admin_name: '', admin_email: '', phone: '', org_number: '',
    account_type: 'paying', plan_template: 'pro', contract_months: 12, coupon: '',
    employee_count: 1, billing_method: 'card', send_agreement: true, custom_features: {},
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.saListPlans().then(res => setPlans(res.data || [])).catch(() => {});
  }, []);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const selectedPlan = useMemo(() => plans.find(p => p.name?.toLowerCase() === form.plan_template), [plans, form.plan_template]);

  const contractMult = useMemo(() => CONTRACT_OPTIONS.find(c => c.months === form.contract_months)?.multiplier || 1, [form.contract_months]);

  const monthlyPrice = useMemo(() => {
    if (!selectedPlan) return 0;
    const base = Math.round(selectedPlan.monthly_price * contractMult);
    const extras = Math.max(0, (form.employee_count || 1) - 1);
    return base + extras * (PLAN_PER_EMPLOYEE[selectedPlan.name.toLowerCase()] || 0);
  }, [selectedPlan, contractMult, form.employee_count]);

  const submit = async () => {
    if (!form.company_name || !form.admin_name || !form.admin_email) {
      toast.error('Fyll i företagsnamn, namn och e-post');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      const res = await api.salesCreateCustomer(payload);
      setCreated(res.data);
      toast.success(`Kund ${form.company_name} skapad!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa kund');
    } finally { setSaving(false); }
  };

  if (created) {
    return (
      <div className="max-w-xl mx-auto bg-card border border-border rounded-xl p-8 text-center" data-testid="sales-create-success">
        <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={28} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold">Kund skapad!</h2>
        <p className="text-sm text-muted-foreground mt-2">
          <strong>{form.company_name}</strong> är registrerad med din säljkod <span className="font-mono">{user?.sales_code}</span>.<br />
          Aktiveringsmail skickas till {form.admin_email}.
        </p>
        <div className="bg-muted/30 rounded-lg p-4 mt-4 text-left text-xs space-y-1">
          <div className="flex justify-between"><span className="text-muted-foreground">Paket</span><span className="font-semibold">{created.plan_label}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Månadspris</span><span className="font-semibold">{Math.round(created.monthly_price).toLocaleString('sv-SE')} kr</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Säljkod</span><span className="font-mono font-semibold">{user?.sales_code}</span></div>
        </div>
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={() => { setCreated(null); setForm({ ...form, company_name: '', admin_name: '', admin_email: '', org_number: '', phone: '' }); }} className="flex-1" data-testid="sales-create-another">
            Skapa en till
          </Button>
          <Button onClick={() => window.location.href = '/sales/customers'} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white">
            Mina kunder <ArrowRight size={12} className="ml-1.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="sales-new-customer-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserPlus size={22} className="text-[#E8613A]" /> Ny kund
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Kunder du skapar taggas automatiskt med din säljkod <span className="font-mono font-semibold">{user?.sales_code}</span>.
        </p>
      </div>

      <section className="bg-card border border-border rounded-xl p-5 space-y-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#E8613A]">Kontaktinfo</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Företagsnamn *</Label>
            <Input value={form.company_name} onChange={(e) => update('company_name', e.target.value)} className="mt-1" data-testid="sales-company-name" />
          </div>
          <div>
            <Label className="text-xs">Organisationsnummer</Label>
            <Input value={form.org_number} onChange={(e) => update('org_number', e.target.value)} placeholder="556789-1234" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Kontaktperson *</Label>
            <Input value={form.admin_name} onChange={(e) => update('admin_name', e.target.value)} className="mt-1" data-testid="sales-admin-name" />
          </div>
          <div>
            <Label className="text-xs">E-post *</Label>
            <Input type="email" value={form.admin_email} onChange={(e) => update('admin_email', e.target.value)} className="mt-1" data-testid="sales-admin-email" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Telefon</Label>
            <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-1" />
          </div>
        </div>
      </section>

      <section className="bg-card border border-border rounded-xl p-5 space-y-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#E8613A]">Paket & Bindningstid</p>
        <div className="grid grid-cols-3 gap-2">
          {plans.map(p => {
            const active = form.plan_template === p.name.toLowerCase();
            return (
              <button key={p.name} type="button" onClick={() => update('plan_template', p.name.toLowerCase())}
                className={`p-3 rounded-lg border-2 text-left ${active ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border'}`}
                data-testid={`sales-plan-${p.name.toLowerCase()}`}>
                <p className="text-sm font-bold">{p.name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{Math.round(p.monthly_price).toLocaleString('sv-SE')} kr/mån <span className="opacity-70">exkl. moms</span></p>
              </button>
            );
          })}
        </div>

        <div>
          <Label className="text-xs">Bindningstid</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {CONTRACT_OPTIONS.map(o => {
              const active = form.contract_months === o.months;
              return (
                <button key={o.months} type="button" onClick={() => update('contract_months', o.months)}
                  className={`p-3 rounded-lg border-2 ${active ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border'}`}
                  data-testid={`sales-contract-${o.months}`}>
                  <p className="font-bold text-sm">{o.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{o.badge}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Antal anställda</Label>
            <Input type="number" min={1} value={form.employee_count} onChange={(e) => update('employee_count', parseInt(e.target.value) || 1)} className="mt-1" data-testid="sales-employee-count" />
          </div>
          <div>
            <Label className="text-xs">Rabattkod (valfri)</Label>
            <Input value={form.coupon} onChange={(e) => update('coupon', e.target.value.trim().toUpperCase())} placeholder="LAUNCH50" className="mt-1" data-testid="sales-coupon" />
          </div>
        </div>

        <div className="bg-[#E8613A]/5 border border-[#E8613A]/30 rounded-lg p-4 flex justify-between items-center">
          <p className="text-xs uppercase tracking-wider font-bold text-[#E8613A]">Totalt månadspris</p>
          <p className="text-2xl font-bold" data-testid="sales-total-price">{monthlyPrice.toLocaleString('sv-SE')} kr/mån <span className="text-xs font-normal text-muted-foreground">exkl. 25 % moms</span></p>
        </div>
      </section>

      <div className="flex gap-2">
        <Button onClick={submit} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full px-6" data-testid="sales-create-customer-btn">
          {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
          {saving ? 'Skapar…' : 'Skapa kund'}
        </Button>
      </div>
    </div>
  );
};

export default SalesNewCustomer;
